import type { AppointmentTechnician } from '../stores/useAppointmentsStore';

/**
 * Categorical route palette — one deterministic color per technician, shared
 * across all map surfaces (hospitality dot, polyline, map position dot, popover
 * avatar ring, trigger avatar).
 *
 * The first five are the canonical sketch-6.3 order (blue, purple, emerald,
 * rose, amber — D-04); the remaining fifteen extend the set to twenty distinct
 * hues so larger crews still get a unique color.
 *
 * Theme-safety: every entry is a mid-tone Tailwind 500/600 value, chosen to
 * read clearly as a stroke on BOTH the light map tiles and a dark-themed map.
 * The yellow/lime hues are deliberately taken at the 600 tone — their 500 tones
 * wash out against white tiles. Adjacent indices contrast strongly (the early
 * indices, used by typical 2–4 tech crews, are maximally distinct).
 *
 * Frozen so consumers cannot mutate the shared palette.
 */
export const PALETTE = Object.freeze([
  '#3b82f6', // blue-500
  '#a855f7', // purple-500
  '#10b981', // emerald-500
  '#f43f5e', // rose-500
  '#f59e0b', // amber-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#65a30d', // lime-600
  '#6366f1', // indigo-500
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#d946ef', // fuchsia-500
  '#ca8a04', // yellow-600
  '#0ea5e9', // sky-500
  '#ef4444', // red-500
  '#22c55e', // green-500
  '#9333ea', // purple-600 (deep violet)
  '#e11d48', // rose-600 (deep rose)
  '#0d9488', // teal-600 (deep teal)
  '#db2777', // pink-600 (deep pink)
] as const);

/**
 * Maximum number of technicians that receive a route color — one per palette
 * entry. Techs beyond this (in id-ascending order) are dropped (TRACK-03);
 * twenty is far past any real crew size.
 */
const MAX_TECHS = PALETTE.length;

/**
 * A valid CSS hex color: `#` followed by 3, 4, 6, or 8 hex digits.
 *
 * IN-04: a backend `route_color` is interpolated into SVG marker `innerHTML`
 * (TrackingMapCard.vehicleSvg) and into `:style="{ background }"` bindings in
 * the popover/trigger. The source is trusted today, but validating here makes
 * the whole color surface safe-by-construction: any non-hex value (a future
 * tenant/customer-influenced field, or a malformed payload) is rejected before
 * it can reach an `innerHTML` interpolation, and the palette slot is used
 * instead. This is the single choke point every surface resolves color through.
 */
const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Resolve one stable route color per technician.
 *
 * Precedence (D-03, backend-first): a backend-supplied `route_color` wins;
 * otherwise the tech is assigned `PALETTE[index]` by its position in the
 * id-ascending sort (D-04). Because assignment keys off the sorted roster — not
 * insertion order — a technician keeps a deterministic palette slot as the
 * roster reorders, and the slots reshuffle predictably as it grows.
 *
 * The returned Map is keyed PURELY by `AppointmentTechnician.id` (the profile
 * id). It does NOT itself bridge id spaces: live GPS frames are keyed by the
 * technician's USER id, and that reconciliation is performed by the CONSUMER
 * (TrackingMapCard's `colorByUserId`), not by this pure function. A frame keyed
 * by a profile id resolves directly here; a non-matching id returns `undefined`
 * rather than a wrong tech's color (Open Q3 / Pitfall 5).
 *
 * Pure: no Vue, no I/O, no module-level mutable state beyond the frozen PALETTE.
 *
 * @param techs the appointment's technician roster (any order)
 * @returns Map of `technician.id` → hex color string, capped at `PALETTE.length` (20) entries
 */
export function resolveColors(
  techs: AppointmentTechnician[],
): Map<number, string> {
  const sorted = [...techs].sort((a, b) => a.id - b.id).slice(0, MAX_TECHS);
  const out = new Map<number, string>();
  sorted.forEach((t, i) => {
    // IN-04: only honor a backend route_color that is a valid hex string;
    // otherwise fall back to the deterministic palette slot. This keeps any
    // untrusted/malformed value out of the downstream innerHTML interpolation.
    const backend = t.route_color;
    const safe = backend && HEX_COLOR.test(backend) ? backend : PALETTE[i];
    out.set(t.id, safe);
  });
  return out;
}
